import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { i18n } from 'utils/i18n';
import { sanitize } from 'utils/validation';
import { analyticsInteraction } from 'utils/analytics';
import { ANALYTICS } from 'constants';
import Button from 'components/Button';
import MessageBanner from 'components/MessageBanner';
import LimitedInventoryModal from 'components/modals/LimitedInventoryModal';
import LimitedAdaptiveEquipment from 'components/modals/LimitedAdaptiveEquipment';
import Extra from 'components/Extra';

const IndividualExtras = ({
  individualExtras,
  shouldShowTitle,
  customTitle,
  scrollRef,
  isCheckIn,
  toggleExtrasLoading,
  hasADAExtra,
}) => {
  const [hasWarnedAboutLimitedInventoryItem, setHasWarnedAboutLimitedInventoryItem] = useState(false);
  const [hasWarnedAdaptativeEquipementItem, setHasWarnedAdaptativeEquipementItem] = useState(false);
  const [isADAopen, setIsADAopen] = useState(false);

  useEffect(() => {
    setIsADAopen(hasADAExtra);
  }, []);

  const setHasWarnedAboutLimitedInventoryItemToTrue = (value = true) => {
    setHasWarnedAboutLimitedInventoryItem(value);
  };

  const setHasWarnedAboutAdaptativeEquipementToTrue = (value = true) => {
    setHasWarnedAdaptativeEquipementItem(value);
  };

  const toggleADAdevices = () => {
    if (!isADAopen) {
      analyticsInteraction(ANALYTICS.DRAWER, ANALYTICS.ADAPTIVE_DRIVING_DEVICES, ANALYTICS.OPEN);
    }
    setIsADAopen((prev) => !prev);
  };

  const SubSectionTitleTag = shouldShowTitle ? 'h3' : 'h2';
  const { protectionProducts, tripEnhancements, adaEquipment } = individualExtras || {};

  return (
    <>
      <section ref={scrollRef}>
        {shouldShowTitle && <h2>{customTitle || i18n('extras_add_individual_extras')}</h2>}

        <section className='individual-extras individual-extras--protection-products'>
          <SubSectionTitleTag className='individual-extras-subsection-title'>
            {i18n('extras_protection_products')}
          </SubSectionTitleTag>

          {protectionProducts?.length ? (
            protectionProducts.map((extra) => (
              <Extra key={extra.code} toggleExtrasLoading={toggleExtrasLoading} extra={extra} />
            ))
          ) : (
            <MessageBanner
              title={i18n('extras_no_protection_products_available_title')}
              message={i18n('extras_no_protection_products_available_message')}
            />
          )}
        </section>

        <section className='individual-extras individual-extras--trip-enhancements'>
          <SubSectionTitleTag className='individual-extras-subsection-title'>
            {i18n('extras_trip_enhancements')}
          </SubSectionTitleTag>

          {tripEnhancements?.length ? (
            tripEnhancements.map((extra) => (
              <Extra
                key={extra.code}
                toggleExtrasLoading={toggleExtrasLoading}
                hasWarnedAboutLimitedInventoryItem={hasWarnedAboutLimitedInventoryItem}
                hasWarnedAdaptativeEquipementItem={hasWarnedAdaptativeEquipementItem}
                setHasWarnedAboutLimitedInventoryItemToTrue={setHasWarnedAboutLimitedInventoryItemToTrue}
                setHasWarnedAboutAdaptativeEquipementToTrue={setHasWarnedAboutAdaptativeEquipementToTrue}
                extra={extra}
                isCheckIn={isCheckIn}
                showShortDescription={true}
              />
            ))
          ) : (
            <MessageBanner
              title={i18n('extras_no_trip_enhancements_available_title')}
              message={i18n('extras_no_trip_enhancements_available_message')}
            />
          )}
        </section>

        {!!adaEquipment?.length && (
          <section className='individual-extras individual-extras--ada-equipment'>
            <SubSectionTitleTag className='individual-extras-subsection-title'>
              {i18n('extras_adaptive_driving_devices')}{' '}
              <Button
                button={false}
                onClick={toggleADAdevices}
                className={`individual-extras__accordion-icon ${isADAopen ? 'expanded' : ''}`}
                ariaExpanded={isADAopen ? 'true' : 'false'}
                ariaText={`${i18n('extras_adaptive_driving_devices')} ${i18n('progress_bar_extras_title')} ${i18n(
                  'extras_expand_button'
                )}`}
              />
            </SubSectionTitleTag>

            <p
              className='individual-extras__description'
              dangerouslySetInnerHTML={sanitize(i18n('extras_ada_extra_section_description'))}
            ></p>

            {isADAopen &&
              adaEquipment?.map((extra) => (
                <Extra
                  key={extra.code}
                  toggleExtrasLoading={toggleExtrasLoading}
                  hasWarnedAboutLimitedInventoryItem={hasWarnedAboutLimitedInventoryItem}
                  hasWarnedAdaptativeEquipementItem={hasWarnedAdaptativeEquipementItem}
                  setHasWarnedAboutLimitedInventoryItemToTrue={setHasWarnedAboutLimitedInventoryItemToTrue}
                  setHasWarnedAboutAdaptativeEquipementToTrue={setHasWarnedAboutAdaptativeEquipementToTrue}
                  extra={extra}
                  isCheckIn={isCheckIn}
                  showShortDescription={true}
                  isADAEquipment={true}
                />
              ))}
          </section>
        )}
      </section>
      <LimitedInventoryModal />
      <LimitedAdaptiveEquipment />
    </>
  );
};

IndividualExtras.propTypes = {
  // Container Props
  breakpoint: PropTypes.object,
  individualExtras: PropTypes.object,
  hasADAExtra: PropTypes.bool,

  // Parent props
  shouldShowTitle: PropTypes.bool,
  customTitle: PropTypes.string,
  scrollRef: PropTypes.object,
  isCheckIn: PropTypes.bool,
  toggleExtrasLoading: PropTypes.func,
};

export default IndividualExtras;
